/**
 * Completeness comparison for the run-manifest verification banner.
 *
 * Pure logic backing the `CompletenessBanner` component (Req 2.3-2.6). It
 * compares the count of loaded `RepoResult`s (`L`) against the expected count
 * echoed from the run manifest (`E`) and classifies the run as:
 *
 *  - `complete`            when `L == E`            — no delta (Req 2.3)
 *  - `incomplete`          when `L <  E`            — `missing = E - L` (Req 2.4)
 *  - `discrepancy`         when `L >  E`            — `extra   = L - E` (Req 2.5)
 *  - `manifest-unavailable` when `E` is `null`      — the manifest could not be
 *                                                     loaded (Req 2.6)
 *
 * The function is pure and total: it never mutates input and returns a value
 * for every `(loaded, expected)` pair, so it is exhaustively property-testable
 * (see Property 5).
 */

/** The four mutually exclusive completeness classifications (Req 2.3-2.6). */
export type CompletenessStatus =
  | "complete"
  | "incomplete"
  | "discrepancy"
  | "manifest-unavailable";

/**
 * Loaded count equals the expected count: the run is fully present (Req 2.3).
 * No delta is reported.
 */
export interface CompleteResult {
  status: "complete";
  loaded: number;
  expected: number;
}

/**
 * Loaded count is below the expected count: some results are missing (Req 2.4).
 * `missing` is `expected - loaded` (always > 0 here).
 */
export interface IncompleteResult {
  status: "incomplete";
  loaded: number;
  expected: number;
  missing: number;
}

/**
 * Loaded count is above the expected count: there are extra results (Req 2.5).
 * `extra` is `loaded - expected` (always > 0 here).
 */
export interface DiscrepancyResult {
  status: "discrepancy";
  loaded: number;
  expected: number;
  extra: number;
}

/**
 * The manifest could not be loaded, so the expected count is unknown (Req 2.6).
 * Only the loaded count is reported; `expected` is `null`.
 */
export interface ManifestUnavailableResult {
  status: "manifest-unavailable";
  loaded: number;
  expected: null;
}

/**
 * Discriminated union of every completeness outcome. Narrow on `status` to
 * access the status-specific delta (`missing` / `extra`).
 */
export type CompletenessResult =
  | CompleteResult
  | IncompleteResult
  | DiscrepancyResult
  | ManifestUnavailableResult;

/**
 * Classify the completeness of a loaded run against the manifest's expected
 * repository count.
 *
 * @param loaded - The number of `RepoResult`s that loaded successfully (`L`).
 * @param expected - The manifest `repo_count` (`E`), or `null` when the manifest
 *   is unavailable.
 * @returns A {@link CompletenessResult} discriminated on `status` carrying the
 *   loaded/expected counts and any applicable delta (`missing` or `extra`).
 */
export function compareCompleteness(
  loaded: number,
  expected: number | null,
): CompletenessResult {
  // Manifest unavailable: expected count unknown (Req 2.6).
  if (expected === null) {
    return { status: "manifest-unavailable", loaded, expected: null };
  }

  // Fewer loaded than expected: report the missing count (Req 2.4).
  if (loaded < expected) {
    return {
      status: "incomplete",
      loaded,
      expected,
      missing: expected - loaded,
    };
  }

  // More loaded than expected: report the extra count (Req 2.5).
  if (loaded > expected) {
    return {
      status: "discrepancy",
      loaded,
      expected,
      extra: loaded - expected,
    };
  }

  // Loaded equals expected: complete, no delta (Req 2.3).
  return { status: "complete", loaded, expected };
}
